import React, { useState, useEffect, useRef } from "react";
import { Input, Button, Card, Spinner, Divider } from "@heroui/react";
import { Search, UserPlus } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { customersService } from "@/services/customersService";
import { Customer } from "@/types/customers";

interface CustomerSearchProps {
  onSelect: (customer: Customer) => void;
}

export const CustomerSearch: React.FC<CustomerSearchProps> = ({ onSelect }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Efecto para cerrar los resultados al hacer clic fuera del componente
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node)
      ) {
        setShowResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Función para buscar clientes
  const searchCustomers = async (term: string) => {
    if (!term.trim()) {
      setResults([]);

      return;
    }

    setLoading(true);
    try {
      // Intentar buscar por teléfono primero
      const phoneResponse = await customersService.searchCustomers({
        phone: term,
        limit: 5,
      });

      // Si no hay resultados por teléfono, buscar por nombre
      if (phoneResponse.total === 0) {
        const nameResponse = await customersService.searchCustomers({
          name: term,
          limit: 5,
        });

        setResults(nameResponse.customers);
      } else {
        setResults(phoneResponse.customers);
      }
    } catch (error) {
      console.error("Error searching customers:", error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Manejar cambios en el campo de búsqueda
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    setSearchTerm(value);
    setShowResults(true);

    // Debounce the search for better performance
    const debounceTimer = setTimeout(() => {
      searchCustomers(value);
    }, 300);

    return () => clearTimeout(debounceTimer);
  };

  // Manejar la selección de un cliente
  const handleSelectCustomer = (customer: Customer) => {
    onSelect(customer);
    setShowResults(false);
    setSearchTerm(
      `${customer.first_name} ${customer.last_name} - ${customer.phone_number}`,
    );
  };

  // Navegar a la página de creación de cliente
  const handleCreateCustomer = () => {
    navigate("/customers/new", { state: { returnTo: location.pathname } });
  };

  return (
    <div ref={searchContainerRef} className="relative">
      <Input
        endContent={loading ? <Spinner size="sm" /> : null}
        placeholder="Search customer by phone or name"
        startContent={<Search className="text-gray-400" size={18} />}
        value={searchTerm}
        onChange={handleSearchChange}
        onFocus={() => searchTerm.trim() && setShowResults(true)}
      />

      {showResults && (
        <Card className="absolute z-10 w-full mt-1 shadow-lg max-h-60 overflow-y-auto">
          {results.length === 0 ? (
            <div className="p-3 text-center text-gray-500">
              {searchTerm.trim() === "" ? (
                "Enter phone or name to search"
              ) : (
                <>
                  No customers found"
                  <Divider className="my-2" />
                  <Button
                    color="primary"
                    startContent={<UserPlus size={16} />}
                    variant="flat"
                    onPress={handleCreateCustomer}
                  >
                    Create new customer
                  </Button>
                </>
              )}
            </div>
          ) : (
            <div>
              {results.map((customer) => (
                <div
                  key={customer.id}
                  className="p-3 hover:bg-gray-100 cursor-pointer"
                  onClick={() => handleSelectCustomer(customer)}
                >
                  <div className="font-medium">
                    {customer.first_name} {customer.last_name}
                  </div>
                  <div className="text-sm text-gray-600">
                    {customer.phone_number}
                    {customer.email && ` • ${customer.email}`}
                  </div>
                </div>
              ))}
              <Divider />
              <div className="p-2">
                <Button
                  className="w-full"
                  color="primary"
                  size="sm"
                  startContent={<UserPlus size={16} />}
                  variant="flat"
                  onPress={handleCreateCustomer}
                >
                  Create new customer
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};
